import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, Typography, Divider, Button, Grid, Tabs, Tab } from '@mui/material';
import { ArrowLeft, CheckCircle2, FileText as FileTextIcon, ListTodo, RefreshCw, Scale } from 'lucide-react';

function InfoRow({ icon: Icon, title, description, color, bgColor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.08)',
        bgcolor: 'rgba(255,255,255,0.7)'
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
      >
        <Icon size={18} color={color} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

function normalizeSection(value) {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return 'lista';
  if (v === 'lista' || v === 'pratiche') return 'lista';
  if (v === 'prd') return 'prd';
  if (v === 'concordato-minore' || v === 'concordato') return 'concordato-minore';
  if (v === 'liquidazione-controllata' || v === 'liquidazione') return 'liquidazione-controllata';
  if (v === 'esdebitazione') return 'esdebitazione';
  return 'lista';
}

export default function Sovraindebitamento() {
  const navigate = useNavigate();
  const { sezione } = useParams();
  const section = useMemo(() => normalizeSection(sezione), [sezione]);

  const tabs = useMemo(
    () => [
      { key: 'lista', label: 'Lista pratiche', path: '/sovraindebitamento' },
      { key: 'prd', label: 'PRD', path: '/sovraindebitamento/prd' },
      { key: 'concordato-minore', label: 'Concordato minore', path: '/sovraindebitamento/concordato-minore' },
      { key: 'liquidazione-controllata', label: 'Liquidazione controllata', path: '/sovraindebitamento/liquidazione-controllata' },
      { key: 'esdebitazione', label: 'Esdebitazione', path: '/sovraindebitamento/esdebitazione' }
    ],
    []
  );

  const tabValue = useMemo(() => tabs.find((t) => t.key === section)?.key || 'lista', [section, tabs]);

  const content = useMemo(() => {
    if (section === 'prd') {
      return {
        title: 'PRD',
        subtitle: 'Piano di ristrutturazione dei debiti: raccolta dati, documenti, analisi e predisposizione output.',
        blocks: [
          {
            icon: FileTextIcon,
            title: 'Cosa farà',
            description:
              'Raccoglie i dati del debitore e del nucleo, consolida i debiti e la documentazione, costruisce una vista unica del fascicolo e prepara un set di output pronti per bozza/validazione.'
          },
          {
            icon: ListTodo,
            title: 'Input richiesti',
            description:
              'Anagrafiche, elenco creditori, contratti/finanziamenti, attestazioni, redditi, spese, patrimonio, visure, estratti conto, cartelle/avvisi, eventuali procedure pendenti.'
          },
          {
            icon: RefreshCw,
            title: 'Automazioni',
            description:
              'Indicizzazione documenti, estrazione campi chiave, riconciliazione creditori, rilevazione anomalie (mancanze, incongruenze), timeline eventi e scadenziario.'
          }
        ]
      };
    }
    if (section === 'concordato-minore') {
      return {
        title: 'Concordato minore',
        subtitle: 'Preparazione fascicolo, analisi debiti e costruzione checklist/atti per la procedura.',
        blocks: [
          {
            icon: Scale,
            title: 'Cosa farà',
            description:
              'Organizza il fascicolo per procedura, consolida creditori/debiti, prepara la mappa delle contestazioni e delle priorità, e supporta la redazione di output strutturati e verificabili.'
          },
          {
            icon: ListTodo,
            title: 'Checklist documentale',
            description:
              'Documenti identità, stato famiglia, redditi, patrimonio, debiti, atti esecutivi, cartelle/avvisi, prove di notifica, eventuali accordi/rateazioni, comunicazioni con creditori.'
          },
          {
            icon: RefreshCw,
            title: 'Controlli',
            description:
              'Coerenza anagrafica, completezza prove e date, collegamento atti presupposti, segnalazione buchi informativi e promemoria operativi per integrazioni.'
          }
        ]
      };
    }
    if (section === 'liquidazione-controllata') {
      return {
        title: 'Liquidazione controllata',
        subtitle: 'Inventario documentale e patrimoniale, eventi/atti e tracciamento scadenze.',
        blocks: [
          {
            icon: RefreshCw,
            title: 'Cosa farà',
            description:
              'Centralizza la documentazione e costruisce una vista “patrimonio/creditori/atti” con collegamenti e timeline, rendendo più semplice la verifica di completezza e la gestione operativa.'
          },
          {
            icon: FileTextIcon,
            title: 'Output',
            description:
              'Elenco strutturato dei documenti, quadro riassuntivo dei debiti e dei creditori, evidenze su atti/procedure (pignoramenti, fermi, ipoteche), e check scadenze.'
          },
          {
            icon: ListTodo,
            title: 'Scadenziario',
            description:
              'Genera promemoria e scadenze operative (da validare) sulla base di eventi rilevati nei documenti e sulle milestone della procedura.'
          }
        ]
      };
    }
    if (section === 'esdebitazione') {
      return {
        title: 'Esdebitazione',
        subtitle: 'Raccolta prove, verifica completezza, riepilogo motivato e report cliente.',
        blocks: [
          {
            icon: CheckCircle2,
            title: 'Cosa farà',
            description:
              'Costruisce un riepilogo strutturato del fascicolo, evidenzia i documenti chiave e le mancanze, e prepara un report consultabile con motivazioni e citazioni delle fonti.'
          },
          {
            icon: ListTodo,
            title: 'Input richiesti',
            description:
              'Documentazione completa della procedura, provvedimenti, comunicazioni, eventuali pagamenti/adempimenti, situazione aggiornata creditori e ogni documento rilevante per la valutazione.'
          },
          {
            icon: FileTextIcon,
            title: 'Report cliente',
            description:
              'Riepilogo debito, stato procedura, rischi/azioni consigliate, checklist “cosa manca” e allegati collegati per tracciabilità.'
          }
        ]
      };
    }
    return {
      title: 'Lista pratiche',
      subtitle: 'Sezione predisposta per gestione pratiche, workflow, scadenze e documentazione.',
      blocks: [
        {
          icon: ListTodo,
          title: 'Cosa farà',
          description: 'Elenco pratiche, creazione pratica, stato e filtri, collegamento documenti, scadenze e task.'
        },
        {
          icon: FileTextIcon,
          title: 'Documenti',
          description: 'Upload e indicizzazione documenti, collegamenti tra fascicoli e allegati, ricerca e tag.'
        },
        {
          icon: RefreshCw,
          title: 'Workflow',
          description: 'Step guidati per PRD, Concordato minore, Liquidazione controllata, Esdebitazione con checklist.'
        }
      ]
    };
  }, [section]);

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ p: 2, mb: 2 }}>
        <Button startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/')}>
          Dashboard
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
          Sovraindebitamento
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {content.subtitle}
        </Typography>
      </Card>

      <Card sx={{ p: 1, mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, nextValue) => {
            const next = tabs.find((t) => t.key === nextValue);
            if (next) navigate(next.path);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((t) => (
            <Tab key={t.key} label={t.label} value={t.key} />
          ))}
        </Tabs>
      </Card>

      <Card sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {content.title}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={1.5}>
          {content.blocks.map((b) => (
            <Grid key={b.title} item xs={12} md={6}>
              <InfoRow
                icon={b.icon}
                title={b.title}
                description={b.description}
                color="#1d4ed8"
                bgColor="#dbeafe"
              />
            </Grid>
          ))}
        </Grid>
      </Card>
    </Box>
  );
}

